// src/encounter/template-drift-notice.tsx
// PROD-2608 — what a reopened note says about its template, above the step canvas.
//
// Two things, each shown only when true:
//   1. A BANNER when the form on screen is not the template the note was started on — it was edited
//      in place since (`changed`), or it can no longer be opened and another was picked
//      (`unavailable`). Non-blocking: the note stays readable, editable (or amendable) as before.
//   2. "ADDITIONAL DOCUMENTATION": every saved answer the form has no place for, read-only, with the
//      question it answered. These are never dropped (`lib/orphaned-answers.ts`); this is where a
//      clinician sees them. The generated note carries the same list (`orphanedAnswersNoteSection`,
//      in lib/orphaned-answers.ts).
//
// Nothing renders when the template is unchanged and every answer has a home.
import { Banner } from '@scripsteam/scrips-signal-ds';
import { formatDate } from '@/shared/lib/date-format';
import { useDesignV2 } from './lib/design-v2';
import type { TemplateDrift, TemplatePin } from './lib/template-pin';
import { ORPHANED_ANSWERS_LABEL, type OrphanedAnswer } from './lib/orphaned-answers';

// Token classes only (the DS shadow-component guard allows no inline styles here).
const WRAP = 'flex flex-col gap-2';
const PANEL = 'border border-solid border-border-default rounded-md bg-surface-card p-4 font-sans';
/** Design sheet v1.1 §1 Surfaces (switch ON only): a card on the canvas lifts. */
const PANEL_V2 = 'border border-solid border-border-card rounded-md bg-surface-card p-4 font-sans shadow-2';
const HEADING = 'm-0 text-size-sm font-bold text-text-primary';
const HINT = 'my-2 text-size-sm text-text-secondary';
const LIST = 'm-0 text-size-sm';
const ROW = 'mt-2';
const LABEL = 'text-text-secondary';
const VALUE = 'm-0 text-text-primary';

export interface TemplateDriftNoticeProps {
  drift: TemplateDrift | 'pending';
  /** The note's pin (for the version it was started on). */
  pin: TemplatePin | null;
  /** The title of the template now on screen. */
  templateTitle?: string | null;
  orphans: readonly OrphanedAnswer[];
}

function startedOn(pin: TemplatePin | null): string {
  const parts: string[] = [];
  if (pin?.version) parts.push(pin.version);
  if (pin?.templateUpdated) {
    const d = new Date(pin.templateUpdated);
    if (!Number.isNaN(d.getTime())) parts.push(`saved ${formatDate(d)}`);
  }
  return parts.length ? ` (${parts.join(', ')})` : '';
}

function orphanSentence(n: number): string {
  return n === 0
    ? ''
    : ` ${n === 1 ? 'One saved answer no longer matches' : `${n} saved answers no longer match`} a question on this form and ${n === 1 ? 'is' : 'are'} shown under ${ORPHANED_ANSWERS_LABEL}. Nothing has been lost.`;
}

export function TemplateDriftNotice({ drift, pin, templateTitle, orphans }: TemplateDriftNoticeProps) {
  const designV2 = useDesignV2();
  const title = templateTitle?.trim() || 'this template';
  const banner =
    drift === 'changed' ? (
      <Banner variant="warning" title="This template has changed since the note was started">
        {`This note was started on an earlier version of ${title}${startedOn(pin)}; the form below is the current version.`}
        {orphanSentence(orphans.length)}
      </Banner>
    ) : drift === 'unavailable' ? (
      <Banner variant="warning" title="The template this note was started on is no longer available">
        {`This note was started on a template${startedOn(pin)} that can no longer be opened; the form below is ${title}.`}
        {orphanSentence(orphans.length)}
      </Banner>
    ) : null;

  if (!banner && orphans.length === 0) return null;
  return (
    <div data-testid="template-drift-notice" className={WRAP}>
      {banner}
      {orphans.length > 0 && (
        <section
          aria-label={ORPHANED_ANSWERS_LABEL}
          data-testid="orphaned-answers"
          className={designV2 ? PANEL_V2 : PANEL}
        >
          <h3 className={HEADING}>
            {ORPHANED_ANSWERS_LABEL}
          </h3>
          <p className={HINT}>
            Saved on this note, with no matching question on the current form. Kept as recorded.
          </p>
          <dl className={LIST}>
            {orphans.map((o) => (
              <div key={o.linkId} data-link-id={o.linkId} className={ROW}>
                <dt className={LABEL}>{o.label}</dt>
                <dd className={VALUE}>{o.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}
